import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  MoreVertical,
  ArrowUp,
  Pencil,
  RefreshCw,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { collectionsApi } from '@/api/collectionsApi';
import { useToast } from '@/hooks/useToast';
import { TaxonomyModal } from '@/components/catalog/TaxonomyModal';
import type { ProductCollection } from '@/types/api';

interface CollectionsProps {
  embedded?: boolean;
}

export function Collections({ embedded = false }: CollectionsProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ProductCollection | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const { data: items, isLoading } = useQuery({
    queryKey: ['collections'],
    queryFn: () => collectionsApi.getAll(true),
  });

  const seedMutation = useMutation({
    mutationFn: collectionsApi.seedDefaults,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      success('Collections Added', 'Default collections have been added.', 5000);
    },
    onError: () => {
      error('Failed', 'Could not add default collections.');
    },
  });

  const handleSuccess = () => {
    success(
      editingItem ? 'Collection Updated' : 'Collection Created',
      editingItem
        ? 'Collection has been updated successfully.'
        : 'New collection has been added.',
      5000
    );
    setEditingItem(null);
  };

  const handleEdit = (item: ProductCollection) => {
    setEditingItem(item);
    setShowModal(true);
    setActiveMenu(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-warm-cream">Collections</h1>
            <p className="text-gray-600 dark:text-warm-sand mt-1">
              Manage audience and themed groupings for your products
            </p>
          </div>
        )}
        <div className={`flex gap-3 ${embedded ? 'sm:ml-auto' : ''}`}>
          <button
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
            className="btn-secondary"
          >
            {seedMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Reset Defaults
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            Add Collection
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-12 text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-amber-450/30 border-t-amber-450 rounded-full animate-spin" />
            <p className="text-gray-600 dark:text-warm-sand">Loading collections...</p>
          </div>
        </div>
      ) : items?.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-charcoal-700 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-gray-600 dark:text-warm-sand" />
            </div>
            <div>
              <p className="text-gray-900 dark:text-warm-cream font-medium text-lg">No collections yet</p>
              <p className="text-gray-600 dark:text-warm-sand text-sm mt-1">
                Add your first collection or reset to defaults
              </p>
            </div>
            <div className="flex gap-3 mt-2">
              <button onClick={() => seedMutation.mutate()} className="btn-secondary">
                <RefreshCw className="w-4 h-4 mr-2" />
                Add Defaults
              </button>
              <button onClick={() => setShowModal(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Collection
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items?.map((item, index) => (
            <div
              key={item.id}
              className={`card p-5 transition-all ${
                item.isActive ? 'hover:border-amber-450/30' : 'opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-450/10 text-2xl">
                    {item.icon || '✨'}
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-warm-cream">
                      {item.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {item.isActive ? (
                        <span className="badge-success text-xs">Active</span>
                      ) : (
                        <span className="badge-danger text-xs">Inactive</span>
                      )}
                      <span className="text-xs text-gray-600 dark:text-warm-sand">Order: {item.sortOrder}</span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors"
                    onClick={() => setActiveMenu(activeMenu === item.id ? null : item.id)}
                  >
                    <MoreVertical className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                  </button>

                  {activeMenu === item.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setActiveMenu(null)} />
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg shadow-lg z-20 py-1">
                        <button
                          onClick={() => handleEdit(item)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 flex items-center gap-2"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {item.description && (
                <p className="text-sm text-gray-600 dark:text-warm-sand mb-4 line-clamp-2">
                  {item.description}
                </p>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-charcoal-700">
                <div className="flex-1" />
                <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-warm-sand">
                  <ArrowUp className="w-3 h-3" />
                  <span>{index + 1}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <TaxonomyModal
        kind="collection"
        isOpen={showModal}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        item={editingItem}
        createFn={(data) => collectionsApi.create(data)}
        updateFn={(id, data) => collectionsApi.update(id, data)}
        queryKey="collections"
      />
    </div>
  );
}
